/**
 * Script Otimizado para Alteração Dinâmica de Preços - Shopify
 * Versão: 2.5 - Suporte Completo a Páginas de Produto Individual
 *
 * Funcionalidades:
 * - Atualização dinâmica de preços baseada em variantes
 * - Formatação correta para padrão brasileiro (R$ 2.994,00)
 * - Classes verdes, elementos de PIX e desconto mantidos
 * - Remoção de mensagens "Translation missing"
 * - Prevenção de FOUC (Flash of Unstyled Content)
 * - Otimização para CLS (Cumulative Layout Shift)
 * - Proteção robusta que impede o tema de remover a div de preço na home page.
 * - Layout completo aplicado em páginas de produto individual (/products/xxxx)
 */

(function () {
    'use strict';

    // ... (O restante das configurações e Utils permanece o mesmo do seu script original) ...
    // Configurações globais
    const CONFIG = {
        // Configuração de desconto PIX
        pixDiscountRate: 0.04, // 4% de descont
        pixColor: '#32CD32',
        // Configuração de parcelas sem juros
        maxInterestFreeInstallments: 10,
        installmentCount: 10, // Número de parcelas
        // Debounce para performance
        debounceDelay: 100,
        // Percentual adicional para calcular o preço riscado (não usado mais)
        comparePricePercentage: 15,
        // Seletores para diferentes tipos de página
        selectors: {
            // Home page
            productCards: [
    '.product-item',
    '.product-card',
    '.grid-product',
    '.product-grid-item'
],
            // Página de produto
            priceContainer: [
                '.product-item__price-list',
                '.price-list',
                '.product-price',
                '.price',
                '[class*="price"]',
                '.product-form__info-content',
                '.product-form__info-item .product-form__info-content'
            ],
            variantSelect: [
                'select[name="id"]',
                'select[id*="product-select"]',
                'select[id*="variant"]',
                '.variant-selector select',
                '.block-swatch__radio',
                '.product-form__single-selector'
            ],
            comparePrice: [
                '.price--compare',
                '.price__compare',
                '[class*="compare"]'
            ]
        },
        // Configurações de formatação brasileira
        currency: {
            symbol: 'R$',
            decimal: ',',
            thousand: '.',
            precision: 2
        }
    };

    // Utilitários
    const Utils = {
        debounce: function (func, wait) {
            let timeout;
            return function executedFunction(...args) {
                const later = () => {
                    clearTimeout(timeout);
                    func(...args);
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            };
        },
        formatMoney: function (amount, options = {}) {
            const opts = Object.assign({}, CONFIG.currency, options);
            if (typeof amount === 'string') {
                amount = parseFloat(amount.replace(/[^\d,.-]/g, '').replace(',', '.'));
            }
            if (isNaN(amount) || amount == null) return `${opts.symbol} 0${opts.decimal}00`;
            if (amount > 10000 && !options.skipCentConversion) {
                amount = amount / 100;
            }
            const formatted = amount.toFixed(opts.precision);
            const parts = formatted.split('.');
            parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${opts.thousand}`);
            return `${opts.symbol} ${parts.join(opts.decimal)}`;
        },
        extractPrice: function (priceString) {
            if (!priceString) return 0;
            const cleanString = priceString.replace(/[^\d.,]/g, '');
            if (!cleanString) return 0;
            const hasComma = cleanString.includes(',');
            const hasDot = cleanString.includes('.');
            let numericValue;
            if (hasComma && hasDot) {
                numericValue = parseFloat(cleanString.replace(/\./g, '').replace(',', '.'));
            } else if (hasComma && !hasDot) {
                numericValue = parseFloat(cleanString.replace(',', '.'));
            } else if (hasDot && !hasComma) {
                const dotPosition = cleanString.lastIndexOf('.');
                const afterDot = cleanString.substring(dotPosition + 1);
                if (afterDot.length === 2) {
                    numericValue = parseFloat(cleanString);
                } else {
                    numericValue = parseFloat(cleanString.replace(/\./g, ''));
                }
            } else {
                numericValue = parseFloat(cleanString);
            }
            return numericValue || 0;
        },
        calculateDiscount: function (originalPrice, salePrice) {
            if (!originalPrice || !salePrice || originalPrice <= salePrice) return 0;
            // Usa a mesma fórmula do tema para garantir consistência
            return Math.round(100 - (100 * salePrice / originalPrice));
        },
        calculateInstallment: function (price, installments = CONFIG.installmentCount, rate = CONFIG.installmentRate) {
            return (price / installments) * rate;
        },
        findElement: function (selectors, context = document) {
            for (const selector of selectors) {
                const element = context.querySelector(selector);
                if (element) return element;
            }
            return null;
        },
        findElements: function (selectors, context = document) {
            const elements = [];
            for (const selector of selectors) {
                elements.push(...context.querySelectorAll(selector));
            }
            return [...new Set(elements)];
        },
        isHomePage: function () {
            const path = window.location.pathname;
            return path === '/' || path === '/index' || path === '/home' || document.body.classList.contains('template-index');
        },
        isCollectionsPage: function () {
            const path = window.location.pathname;
            // Detecta páginas collections (não produtos individuais)
            return path.startsWith('/collections') && !path.includes('/products/');
        },
        isProductPage: function () {
            const path = window.location.pathname;
            // Detecta páginas de produto individual
            return path.startsWith('/products/') && path.split('/').length > 2;
        },
        isIndividualProductPage: function () {
            const path = window.location.pathname;
            // Detecta páginas de produto individual (/products/xxxx) ou páginas de produto com variantes
            return path.startsWith('/products/') && path.split('/').length >= 3;
        }
    };


    class PriceManager {
        constructor() {
            this.priceCache = new Map();
            this.originalTemplates = new Map();
            this.observers = new Map();
            this.isInitialized = false;
            console.log('🚀 PriceManager v2.5 inicializado - Suporte completo a páginas de produto individual.');
        }

        /**
         * Select principal de variante no formulário (fonte de verdade na maioria dos temas Shopify).
         */
        getVariantSelectElement(card) {
            if (!card || typeof card.querySelector !== 'function') return null;
            const selectors = [
                'select[name="id"]',
                'select#product-select',
                'select[id*="product-select"]',
                'select[id*="ProductSelect"]',
                'select[id*="variant"]',
                '.variant-picker select',
                '.variant-picker__select',
                '.product-form__variants select',
                '.selector-wrapper select',
                'variant-selects select',
                'select[data-variant-select]'
            ];
            for (const sel of selectors) {
                const el = card.querySelector(sel);
                if (el && el.tagName === 'SELECT' && el.options && el.options.length > 0) {
                    return el;
                }
            }
            return null;
        }

        /**
         * Resolve a opção de variante correta: prioriza o valor atual do select/hidden (combinado),
         * e só depois tenta mapear swatches — evita erro com várias opções (cor + tamanho etc.).
         */
        resolveSelectedVariantOption(card, variantSelect) {
            const selectEl = this.getVariantSelectElement(card)
                || (variantSelect && variantSelect.tagName === 'SELECT' ? variantSelect : null);

            const hidden = card.querySelector('input[type="hidden"][name="id"]');
            if (hidden && hidden.value && selectEl) {
                const match = Array.from(selectEl.options).find(o => String(o.value) === String(hidden.value));
                if (match) return match;
            }

            if (selectEl && selectEl.options && selectEl.selectedIndex >= 0) {
                const opt = selectEl.options[selectEl.selectedIndex];
                if (opt && String(opt.value).length > 0) {
                    return opt;
                }
            }

            if (hidden && hidden.value) {
                return {
                    value: hidden.value,
                    dataset: {},
                    textContent: '',
                    closest: () => null
                };
            }

            const blockSwatches = card.querySelectorAll('.block-swatch__radio');
            if (blockSwatches.length > 0) {
                const checkedSwatch = Array.from(blockSwatches).find(s => s.checked);
                if (checkedSwatch) {
                    const form = checkedSwatch.closest('form') || card;
                    const formSelect = this.getVariantSelectElement(form) || selectEl;
                    const selectedVariantId = checkedSwatch.dataset.variantId || checkedSwatch.value;
                    if (formSelect && selectedVariantId) {
                        let opt = Array.from(formSelect.options).find(o =>
                            o.value === selectedVariantId || o.dataset.variantId === selectedVariantId
                        );
                        if (!opt) {
                            opt = Array.from(formSelect.options).find(o => {
                                if (o.dataset.variant) {
                                    try {
                                        const vd = JSON.parse(o.dataset.variant);
                                        return String(vd.id) === String(selectedVariantId);
                                    } catch (e) { /* ignore */ }
                                }
                                return false;
                            });
                        }
                        if (!opt) {
                            opt = Array.from(formSelect.options).find(o =>
                                o.textContent.includes(checkedSwatch.value)
                            );
                        }
                        if (opt) return opt;
                    }
                }
            }

            return null;
        }

        lookupVariantInProductJson(variantId, contextEl) {
            const tryParse = (text) => {
                try {
                    return JSON.parse(text);
                } catch (e) {
                    return null;
                }
            };
            const roots = [];
            if (contextEl && contextEl.querySelectorAll) roots.push(contextEl);
            const mainProduct = document.querySelector('.product-form, [id*="MainProduct"], main');
            if (mainProduct) roots.push(mainProduct);
            roots.push(document);

            for (const root of roots) {
                if (!root || !root.querySelectorAll) continue;
                const scripts = root.querySelectorAll('script[type="application/json"]');
                for (const s of scripts) {
                    const data = tryParse(s.textContent);
                    if (!data) continue;
                    const variants = data.variants || (data.product && data.product.variants);
                    if (!variants || !Array.isArray(variants)) continue;
                    const v = variants.find(x => String(x.id) === String(variantId));
                    if (v && v.price != null) {
                        return {
                            price: Number(v.price) / 100,
                            compare_at_price: v.compare_at_price != null ? Number(v.compare_at_price) / 100 : 0
                        };
                    }
                }
            }

            try {
                const variants = window.meta && window.meta.product && window.meta.product.variants;
                if (variants && Array.isArray(variants)) {
                    const v = variants.find(x => String(x.id) === String(variantId));
                    if (v && v.price != null) {
                        return {
                            price: Number(v.price) / 100,
                            compare_at_price: v.compare_at_price != null ? Number(v.compare_at_price) / 100 : 0
                        };
                    }
                }
            } catch (e) { /* ignore */ }

            return null;
        }

        setupFeaturedProducts() {
    const featuredForms = document.querySelectorAll('.product-form');

    featuredForms.forEach((form, index) => {
        const priceContainer = Utils.findElement(CONFIG.selectors.priceContainer, form);
        if (!priceContainer) return;

        if (priceContainer.hasAttribute('data-featured-processed')) return;

        this.setupProductCard(form, `featured-product-${index}`);
        priceContainer.setAttribute('data-featured-processed', 'true');
    });
}

        setupIndividualProductPage() {
            // Verifica se é uma página com variantes funcionando
            const productForm = document.querySelector('.product-form');
            const variantSelect = document.querySelector('select[name="id"]');

            if (productForm && variantSelect) {
                console.log('🛍️ Página com variantes funcionando - Não configurando layout da home');
                return; // Não configura em páginas com variantes funcionando
            }

            console.log('🛍️ Configurando página de produto individual');

            // Procura por cards de produto na página individual
            const productItems = document.querySelectorAll('.product-item');

            if (productItems.length > 0) {
                console.log(`🛍️ Encontrados ${productItems.length} cards de produto na página individual`);

                productItems.forEach((item, index) => {
                    const priceList = item.querySelector('.product-item__price-list');
                    if (priceList && !priceList.hasAttribute('data-processed')) {
                        // Extrai dados do preço atual
                        const currentPriceElement = priceList.querySelector('.price--highlight span, .price--highlight');
                        const comparePriceElement = priceList.querySelector('.price--compare');

                        if (currentPriceElement) {
                            const currentPrice = Utils.extractPrice(currentPriceElement.textContent);
                            const comparePrice = comparePriceElement ? Utils.extractPrice(comparePriceElement.textContent) : 0;

                            const data = {
                                price: currentPrice,
                                comparePrice: comparePrice,
                                variantId: `individual-product-${index}`,
                                discountPercent: comparePrice > currentPrice ? Math.round(100 - (100 * currentPrice / comparePrice)) : 0
                            };

                            // Aplica o estilo completo da home para produtos individuais
                            const updatedHTML = this.generateUpdatedHTML('', data, true);
                            priceList.innerHTML = updatedHTML;
                            priceList.setAttribute('data-processed', 'true');
                            console.log(`✅ Produto individual ${index + 1} processado com layout completo da home`);
                        }
                    }
                });
            } else {
                // Se não encontrar .product-item, tenta outras estruturas
                const priceContainers = Utils.findElements(CONFIG.selectors.priceContainer);
                if (priceContainers.length > 0) {
                    console.log(`🛍️ Encontrados ${priceContainers.length} containers de preço na página individual`);

                    priceContainers.forEach((container, index) => {
                        if (!container.hasAttribute('data-processed')) {
                            // Extrai dados do preço atual
                            const currentPriceElement = container.querySelector('.price--highlight span, .price--highlight');
                            const comparePriceElement = container.querySelector('.price--compare');

                            if (currentPriceElement) {
                                const currentPrice = Utils.extractPrice(currentPriceElement.textContent);
                                const comparePrice = comparePriceElement ? Utils.extractPrice(comparePriceElement.textContent) : 0;

                                const data = {
                                    price: currentPrice,
                                    comparePrice: comparePrice,
                                    variantId: `individual-product-container-${index}`,
                                    discountPercent: comparePrice > currentPrice ? Math.round(100 - (100 * currentPrice / comparePrice)) : 0
                                };

                                // Aplica o estilo completo da home para produtos individuais
                                const updatedHTML = this.generateUpdatedHTML('', data, true);
                                container.innerHTML = updatedHTML;
                                container.setAttribute('data-processed', 'true');
                                console.log(`✅ Container de preço individual ${index + 1} processado com layout completo da home`);
                            }
                        }
                    });
                }
            }
        }

        /**
         * Página de produto (PDP): aplica o mesmo fluxo de preço/variante usado nos cards em destaque,
         * para o(s) .product-form da página. Sem este método, init() quebra (setupProductPage inexistente)
         * e o bloco principal de preços volta ao layout cru do tema.
         */
        setupProductPage() {
            const forms = document.querySelectorAll('.product-form');
            console.log(`🛍️ setupProductPage: ${forms.length} formulário(s) na PDP`);

            forms.forEach((form, index) => {
                const priceContainer = Utils.findElement(CONFIG.selectors.priceContainer, form);
                if (!priceContainer) {
                    console.warn(`⚠️ setupProductPage: container de preço não encontrado no form ${index}`);
                    return;
                }
                if (priceContainer.hasAttribute('data-pdp-processed')) return;

                this.setupProductCard(form, `pdp-product-${index}`);
                priceContainer.setAttribute('data-pdp-processed', 'true');
            });

            this.setupIndividualProductPage();
        }

        init() {
            if (this.isInitialized) return;
            this.isInitialized = true;

            // Detecta o tipo de página e aplica a lógica correta
            if (Utils.isHomePage()) {
    console.log('🏠 Home page detectada. Aplicando lógica de preço para Home.');
    this.setupHomePage();
    this.setupFeaturedProducts();
    this.setupDynamicContentObserver();
} else if (Utils.isCollectionsPage()) {
                console.log('🛒 Página Collections detectada. Aplicando lógica para Collections.');
                this.setupCollectionsPage();
                this.setupDynamicContentObserver();
            } else if (Utils.isProductPage()) {
    console.log('🛍️ Página de produto detectada. Aplicando lógica única da página de produto.');
    this.setupProductPage();
    this.setupDynamicContentObserver();
} else {
                console.log('🔍 Página não identificada. Aplicando lógica padrão.');
                this.setupProductPage();
                this.setupDynamicContentObserver();
            }
        }

        setupCollectionsPage() {
            const productCards = Utils.findElements(['.product-item']);
            const currentPath = window.location.pathname;
            const collectionName = currentPath.split('/collections/')[1]?.split('/')[0] || 'collection';

            console.log(`🛒 Encontrados ${productCards.length} cards de produto na coleção: ${collectionName}`);

            // Processa produtos do grid padrão
            productCards.forEach((card, index) => {
                let infoInner = card.querySelector('.product-item__info-inner');
                if (!infoInner) {
                    infoInner = card.querySelector('.product-item__info');
                }
                if (!infoInner) {
                    console.warn(`⚠️ .product-item__info-inner e .product-item__info não encontrados para card index ${index}`);
                    console.log(card.outerHTML.substring(0, 200));
                    return;
                }

                const priceContainer = infoInner.querySelector('.product-item__price-list');
                if (!priceContainer) {
                    console.warn(`⚠️ Container de preço não encontrado para card index ${index}`);
                    console.log(card.outerHTML.substring(0, 200));
                    return;
                }

                if (!priceContainer.id) {
                    priceContainer.id = `${collectionName}-price-container-${index}`;
                }

                if (!this.originalTemplates.has(priceContainer.id)) {
                    this.originalTemplates.set(priceContainer.id, priceContainer.innerHTML);
                }

                // Extrai dados do preço atual para aplicar o estilo correto
                const currentPriceElement = priceContainer.querySelector('.price--highlight span, .price--highlight');
                const comparePriceElement = priceContainer.querySelector('.price--compare');

                if (currentPriceElement) {
                    const currentPrice = Utils.extractPrice(currentPriceElement.textContent);
                    const comparePrice = comparePriceElement ? Utils.extractPrice(comparePriceElement.textContent) : 0;

                    const data = {
                        price: currentPrice,
                        comparePrice: comparePrice,
                        variantId: `${collectionName}-${index}`,
                        discountPercent: comparePrice > currentPrice ? Math.round(100 - (100 * currentPrice / comparePrice)) : 0
                    };

                    // Aplica o estilo completo da home para collections (igual ao segundo grid da home)
                    const updatedHTML = this.generateUpdatedHTML(this.originalTemplates.get(priceContainer.id), data, false);
                    priceContainer.innerHTML = updatedHTML;
                    priceContainer.setAttribute('data-processed', 'true');
                    console.log(`✅ Card ${collectionName} ${index} atualizado com estilo completo da home.`);
                } else {
                    console.warn(`⚠️ Elemento de preço não encontrado para card index ${index}`);
                }
            });

            // Processa produtos da lista/tabela dentro de .collection__dynamic-part
            const dynamicPart = document.querySelector('.collection__dynamic-part');
            if (dynamicPart) {
                const listCards = dynamicPart.querySelectorAll('.product-list.product-list--collection .product-item');
                console.log(`🛒 Encontrados ${listCards.length} cards na lista/tabela da coleção ${collectionName}`);

                listCards.forEach((card, index) => {
                    let infoInner = card.querySelector('.product-item__info-inner');
                    if (!infoInner) {
                        infoInner = card.querySelector('.product-item__info');
                    }
                    if (!infoInner) {
                        console.warn(`⚠️ .product-item__info-inner e .product-item__info não encontrados para card na lista/tabela index ${index}`);
                        console.log(card.outerHTML.substring(0, 200));
                        return;
                    }

                    const priceContainer = infoInner.querySelector('.product-item__price-list');
                    if (!priceContainer) {
                        console.warn(`⚠️ Container de preço não encontrado para card na lista/tabela index ${index}`);
                        console.log(card.outerHTML.substring(0, 200));
                        return;
                    }

                    if (!priceContainer.id) {
                        priceContainer.id = `${collectionName}-list-price-container-${index}`;
                    }

                    if (!this.originalTemplates.has(priceContainer.id)) {
                        this.originalTemplates.set(priceContainer.id, priceContainer.innerHTML);
                    }

                    // Extrai dados do preço atual para aplicar o estilo correto
                    const currentPriceElement = priceContainer.querySelector('.price--highlight span, .price--highlight');
                    const comparePriceElement = priceContainer.querySelector('.price--compare');

                    if (currentPriceElement) {
                        const currentPrice = Utils.extractPrice(currentPriceElement.textContent);
                        const comparePrice = comparePriceElement ? Utils.extractPrice(comparePriceElement.textContent) : 0;

                        const data = {
                            price: currentPrice,
                            comparePrice: comparePrice,
                            variantId: `${collectionName}-list-${index}`,
                            discountPercent: comparePrice > currentPrice ? Math.round(100 - (100 * currentPrice / comparePrice)) : 0
                        };

                        // Aplica o estilo completo da home para collections (igual ao segundo grid da home)
                        const updatedHTML = this.generateUpdatedHTML(this.originalTemplates.get(priceContainer.id), data, false);
                        priceContainer.innerHTML = updatedHTML;
                        priceContainer.setAttribute('data-processed', 'true');
                        console.log(`✅ Card lista/tabela ${collectionName} ${index} atualizado com estilo completo da home.`);
                    } else {
                        console.warn(`⚠️ Elemento de preço não encontrado para card lista/tabela index ${index}`);
                    }
                });
            }
        }

        setupHomePage() {
    const productCards = Utils.findElements(CONFIG.selectors.productCards);
    console.log(`🏠 Encontrados ${productCards.length} cards de produto na home`);

    const processedContainers = new Set();

    productCards.forEach((card, index) => {
        const priceContainer = card.querySelector('.product-item__price-list, .price-list');
        if (!priceContainer) return;

        if (processedContainers.has(priceContainer)) return;
        if (priceContainer.hasAttribute('data-processed')) return;
        if (priceContainer.hasAttribute('data-home-processed')) return;

        processedContainers.add(priceContainer);

        const cardId = `home-card-${index}`;
        console.log(`🔄 Configurando card ${cardId}:`, card.className);
        this.setupProductCard(card, cardId);
    });
}

        setupProductCard(card, cardId) {
    const priceContainer = Utils.findElement(CONFIG.selectors.priceContainer, card);
    const mainVariantSelect = this.getVariantSelectElement(card);
    const variantSelect = mainVariantSelect || Utils.findElement(CONFIG.selectors.variantSelect, card);
    const blockSwatches = card.querySelectorAll('.block-swatch__radio');

    // Se não encontrar o container de preço, tenta encontrar na estrutura da home
    if (!priceContainer) {
        console.warn('⚠️ Container de preço não encontrado, tentando estrutura da home...');
        const homePriceContainer = card.querySelector('.product-item__price-list');
        if (homePriceContainer) {
            console.log('✅ Container de preço da home encontrado');
            this.setupHomeProductCard(card, cardId, homePriceContainer);
            return;
        }
    }

    // Se não encontrar container, aborta
    if (!priceContainer) {
        console.warn('⚠️ Container de preço não encontrado');
        return;
    }

    // Adiciona ID único
    if (!priceContainer.id) {
        priceContainer.id = `price-container-${cardId}`;
    }
    const containerId = priceContainer.id;

    // Salva template original
    if (!this.originalTemplates.has(containerId)) {
        this.originalTemplates.set(containerId, priceContainer.innerHTML);
    }

    // ==========================
    // TRATAMENTO PARA PRODUTO SEM VARIANTE
    // ==========================
    const hasHiddenVariantId = !!card.querySelector('input[type="hidden"][name="id"]');
    if (!mainVariantSelect && !variantSelect && blockSwatches.length === 0 && !hasHiddenVariantId) {
        console.log('🟢 Produto simples detectado, aplicando preço direto do DOM');

        const currentPriceElement = priceContainer.querySelector(
            '.price--highlight span, .price--highlight, .price-item--sale, .price-item--regular, .money'
        );

        const comparePriceElement = priceContainer.querySelector(
            '.price--compare, .price-item--regular, .price-item--compare'
        );

        if (!currentPriceElement) {
            console.warn('⚠️ Preço atual não encontrado no DOM para produto simples');
            return;
        }

        const currentPrice = Utils.extractPrice(currentPriceElement.textContent);
        let comparePrice = comparePriceElement ? Utils.extractPrice(comparePriceElement.textContent) : 0;

        if (comparePrice <= currentPrice) {
            comparePrice = 0;
        }

        const data = {
            price: currentPrice,
            comparePrice: comparePrice,
            variantId: `simple-product-${cardId}`,
            discountPercent: comparePrice > currentPrice
                ? Math.round(100 - (100 * currentPrice / comparePrice))
                : 0
        };

        const updatedHTML = this.generateUpdatedHTML(
            this.originalTemplates.get(containerId),
            data,
            Utils.isProductPage()
        );

        priceContainer.innerHTML = updatedHTML;
        priceContainer.setAttribute('data-processed', 'true');
        console.log(`✅ Produto simples ${cardId} atualizado corretamente`);
        return;
    }

    const updatePrice = () => {
        console.log('🔄 Atualizando preço para', containerId);
        this.updateCardPrice(card, containerId, variantSelect);
    };

    const debouncedUpdate = Utils.debounce(updatePrice, 120);

    // Listeners — select principal sempre (fonte de verdade para múltiplas variantes)
    if (mainVariantSelect) {
        mainVariantSelect.addEventListener('change', debouncedUpdate);
    } else if (variantSelect && variantSelect.tagName === 'SELECT') {
        variantSelect.addEventListener('change', debouncedUpdate);
    }

    blockSwatches.forEach(swatch => {
        swatch.addEventListener('change', () => {
            console.log('🔄 Block swatch alterado');
            setTimeout(debouncedUpdate, 80);
        });
    });

    card.addEventListener('click', (e) => {
        const variantOption = e.target.closest('[data-value], .variant-input, .variant-option, .block-swatch__item, .variant-picker__option, [data-variant-id]');
        if (variantOption) {
            console.log('👆 Clique detectado em variação');
            setTimeout(debouncedUpdate, 80);
        }
    });

    const observer = new MutationObserver(() => {
        if (!card.querySelector(`#${containerId}`)) {
            console.warn(`🛡️ Container de preço #${containerId} foi removido pelo tema. Restaurando...`);
            this.restorePriceContainer(card, containerId, variantSelect);
        } else {
            const currentContainer = card.querySelector(`#${containerId}`);
            if (currentContainer.hasAttribute('data-shopify-updated')) {
                currentContainer.removeAttribute('data-shopify-updated');
                console.log(`🔄 Tema atualizou #${containerId}. Reaplicando nosso preço.`);
                updatePrice();
            }
        }
    });

    observer.observe(card, { childList: true, subtree: true });
    this.observers.set(containerId, observer);

    updatePrice();
}

        // Método específico para configurar cards da home
        setupHomeProductCard(card, cardId, priceContainer) {
            // Armazena o HTML original para restauração
            if (!this.originalTemplates.has(cardId)) {
                this.originalTemplates.set(cardId, priceContainer.innerHTML);
            }

            // Extrai dados do preço atual
            const currentPriceElement = priceContainer.querySelector('.price--highlight span');
            const comparePriceElement = priceContainer.querySelector('.price--compare');
            const parcelamentoElement = priceContainer.querySelector('.parcelamento');

            if (currentPriceElement) {
                const currentPrice = Utils.extractPrice(currentPriceElement.textContent);
                const comparePrice = comparePriceElement ? Utils.extractPrice(comparePriceElement.textContent) : 0;

                const data = {
                    price: currentPrice,
                    comparePrice: comparePrice,
                    variantId: cardId,
                    discountPercent: comparePrice > currentPrice ? Math.round(100 - (100 * currentPrice / comparePrice)) : 0
                };

                // Aplica o estilo correto baseado no tipo de página
                const updatedHTML = this.generateUpdatedHTML(this.originalTemplates.get(cardId), data, Utils.isProductPage());
                priceContainer.innerHTML = updatedHTML;
                console.log(`✅ Card da home ${cardId} configurado com estilo correto`);
            }
        }

        // <-- ALTERAÇÃO: Nova função para restaurar a div de preço
        restorePriceContainer(card, containerId, variantSelect) {
            const originalTemplate = this.originalTemplates.get(containerId);
            if (!originalTemplate) return;

            // Encontra o local onde a div de preço deveria estar (geralmente perto do seletor de variantes)
            const anchor = (this.getVariantSelectElement(card) || Utils.findElement(CONFIG.selectors.variantSelect, card))?.closest('div, fieldset');
            if (anchor) {
                const newContainer = document.createElement('div');
                // Recria o container com as classes e ID originais
                newContainer.className = 'price-list'; // Use a classe mais comum ou ajuste conforme seu tema
                newContainer.id = containerId;
                newContainer.innerHTML = originalTemplate;

                // Força a visibilidade do novo container
                newContainer.style.setProperty('display', 'block', 'important');
                newContainer.style.setProperty('visibility', 'visible', 'important');
                newContainer.style.setProperty('opacity', '1', 'important');

                // Insere o container restaurado de volta no DOM
                anchor.parentNode.insertBefore(newContainer, anchor.nextSibling);
                console.log(`✅ Container #${containerId} restaurado.`);

                // Re-aplica o preço correto para a variante selecionada
                this.updateCardPrice(card, containerId, variantSelect);
            }
        }

        updateCardPrice(card, containerId, variantSelect) {
    let priceContainer = card.querySelector(`#${containerId}`);
    if (!priceContainer) {
        console.warn(`⚠️ Container ${containerId} não encontrado. Tentando restaurar...`);
        this.restorePriceContainer(card, containerId, variantSelect);
        priceContainer = card.querySelector(`#${containerId}`);
        if (!priceContainer) return;
    }

    priceContainer.style.setProperty('display', 'block', 'important');
    priceContainer.style.setProperty('visibility', 'visible', 'important');
    priceContainer.style.setProperty('opacity', '1', 'important');

    const priceTitle = priceContainer.querySelector('.product-form__info-title.text--strong');
    if (priceTitle) {
        priceTitle.style.setProperty('display', 'none', 'important');
    }

    const selectedOption = this.resolveSelectedVariantOption(card, variantSelect);

    // ==========================
    // FALLBACK PARA PRODUTO SEM VARIANTE
    // ==========================
    if (!selectedOption) {
        console.log('🟢 Sem opção selecionada: tentando fallback do DOM');

        const currentPriceElement = priceContainer.querySelector(
            '.price--highlight span, .price--highlight, .price-item--sale, .price-item--regular, .money'
        );

        const comparePriceElement = priceContainer.querySelector(
            '.price--compare, .price-item--regular, .price-item--compare'
        );

        if (!currentPriceElement) {
            console.warn('⚠️ Nenhuma opção selecionada e preço do DOM não encontrado');
            return;
        }

        const currentPrice = Utils.extractPrice(currentPriceElement.textContent);
        let comparePrice = comparePriceElement ? Utils.extractPrice(comparePriceElement.textContent) : 0;

        if (comparePrice <= currentPrice) {
            comparePrice = 0;
        }

        const data = {
            price: currentPrice,
            comparePrice: comparePrice,
            variantId: 'simple-product-fallback',
            discountPercent: comparePrice > currentPrice
                ? Math.round(100 - (100 * currentPrice / comparePrice))
                : 0
        };

        const originalTemplate = this.originalTemplates.get(containerId) || priceContainer.innerHTML;
        const updatedHTML = this.generateUpdatedHTML(originalTemplate, data, Utils.isProductPage());

        priceContainer.innerHTML = updatedHTML;
        priceContainer.style.setProperty('display', 'block', 'important');
        priceContainer.style.setProperty('visibility', 'visible', 'important');
        priceContainer.style.setProperty('opacity', '1', 'important');
        return;
    }

    const variantData = this.extractVariantData(selectedOption, card);
    if (!variantData || !variantData.price) {
        console.warn('⚠️ Dados de preço não encontrados');
        return;
    }

    const apply = () => {
        const originalTemplate = this.originalTemplates.get(containerId) || priceContainer.innerHTML;
        const updatedHTML = this.generateUpdatedHTML(originalTemplate, variantData, Utils.isProductPage());

        priceContainer.innerHTML = updatedHTML;
        priceContainer.style.setProperty('display', 'block', 'important');
        priceContainer.style.setProperty('visibility', 'visible', 'important');
        priceContainer.style.setProperty('opacity', '1', 'important');

        const priceElements = priceContainer.querySelectorAll('.price, .price--compare, .price__discount');
        priceElements.forEach(el => {
            el.style.setProperty('display', 'block', 'important');
            el.style.setProperty('visibility', 'visible', 'important');
            el.style.setProperty('opacity', '1', 'important');
        });
    };

    requestAnimationFrame(() => requestAnimationFrame(apply));
}

        extractVariantData(option, contextRoot) {
            const value = option.value;
            // if (this.priceCache.has(value)) {
            //     return this.priceCache.get(value);
            // }

            let variantPrice = 0;
            let compareAtPrice = 0;

            console.log(`[PriceManager] Extracting data for variant ${value}`);

            // --- MÉTODO 1: JSON de dados da variante na própria <option> ---
            // Esta é a forma mais robusta se o tema a suportar.
            if (option.dataset.variant) {
                try {
                    const variantData = JSON.parse(option.dataset.variant);
                    variantPrice = variantData.price / 100;
                    compareAtPrice = variantData.compare_at_price ? variantData.compare_at_price / 100 : 0;
                    console.log(`[PriceManager] ✅ Preços encontrados no JSON da option:`, { variantPrice, compareAtPrice });
                } catch (e) {
                    console.warn(`[PriceManager] ❌ Erro ao parsear JSON da variante:`, e);
                }
            }

            // --- MÉTODO 2: Atributos de dados individuais na <option> ---
            if (!variantPrice && option.dataset.price) {
                variantPrice = parseFloat(option.dataset.price) / 100;
                compareAtPrice = option.dataset.compareAtPrice ? parseFloat(option.dataset.compareAtPrice) / 100 : 0;
                console.log(`[PriceManager] ✅ Preços encontrados nos datasets da option:`, { variantPrice, compareAtPrice });
            }

            // --- MÉTODO 3: JSON de dados de TODAS as variantes no <select> ---
            // Este método é bom, mas depende de encontrar o 'value' correto no JSON.
            if (!variantPrice && option.closest('select')) {
                const select = option.closest('select');
                if (select.dataset.variantPrices) {
                    try {
                        const prices = JSON.parse(select.dataset.variantPrices);
                        if (prices[value]) {
                            variantPrice = prices[value].price / 100;
                            compareAtPrice = prices[value].compare_at_price ? prices[value].compare_at_price / 100 : 0;
                            console.log(`[PriceManager] ✅ Preços encontrados no dataset do select:`, { variantPrice, compareAtPrice });
                        }
                    } catch (e) {
                        console.warn(`[PriceManager] ❌ Erro ao parsear JSON de preços do select:`, e);
                    }
                }
            }

            // --- MÉTODO 3b: JSON do produto na página (variant id na option, sem data-variant) ---
            if (!variantPrice && value) {
                const jsonRoot = contextRoot
                    || (typeof option.closest === 'function' ? option.closest('form') : null)
                    || document.querySelector('.product-form, [data-product], [id*="MainProduct"]');
                const fromJson = this.lookupVariantInProductJson(value, jsonRoot);
                if (fromJson) {
                    variantPrice = fromJson.price;
                    compareAtPrice = fromJson.compare_at_price || 0;
                    console.log(`[PriceManager] ✅ Preços no JSON do produto (variante ${value}):`, { variantPrice, compareAtPrice });
                }
            }

            // --- MÉTODO 4: Extração do texto da opção (Último Recurso) ---
            // Este é o método mais frágil e propenso a erros.
            if (!variantPrice) {
                const text = option.textContent || option.innerText || '';
                console.log(`[PriceManager] 🔍 Tentando extrair preços do texto: "${text}"`);
                const priceMatches = text.match(/R\$\s*[\d.,]+/g) || [];

                if (priceMatches.length > 1) {
                    compareAtPrice = Utils.extractPrice(priceMatches[0]);
                    variantPrice = Utils.extractPrice(priceMatches[1]);
                    console.log(`[PriceManager] ✅ Preços extraídos do texto (com desconto):`, { variantPrice, compareAtPrice });
                } else if (priceMatches.length === 1) {
                    variantPrice = Utils.extractPrice(priceMatches[0]);
                    console.log(`[PriceManager] ✅ Preço único extraído do texto:`, variantPrice);
                }
            }

            // Ensure we have valid numeric prices
            variantPrice = parseFloat(variantPrice);
            compareAtPrice = parseFloat(compareAtPrice);

            // If the prices are in cents (> 10000), convert to dollars
            if (variantPrice > 10000) {
                variantPrice = variantPrice / 100;
            }
            if (compareAtPrice > 10000) {
                compareAtPrice = compareAtPrice / 100;
            }

            // Verifica a disponibilidade da variante
            let disponibilidade = "Disponível";
            if (option.dataset.inventory) {
                const inventory = parseInt(option.dataset.inventory);
                if (inventory <= 3 && inventory > 0) {
                    disponibilidade = `Apenas ${inventory} unidades restantes`;
                } else if (inventory <= 0) {
                    disponibilidade = "Indisponível";
                }
            }

            const data = {
                price: variantPrice || 0,
                comparePrice: compareAtPrice || 0,
                variantId: value,
                discountPercent: 0,
                disponibilidade: disponibilidade
            };

            // --- CÁLCULO FINAL E VALIDAÇÃO ---
            // Mantém o preço de comparação original apenas se for maior que o preço atual
            if (data.comparePrice <= data.price || data.comparePrice === 0) {
                data.comparePrice = 0;
            }

            // Ensure we have a valid price
            if (data.price <= 0) {
                console.warn(`[PriceManager] ⚠️ Invalid price detected for variant ${value}:`, data);
                return null;
            }

            // Calcula o desconto apenas se tivermos um preço de comparação real
            if (data.comparePrice > data.price) {
                // Calcula o desconto usando a mesma fórmula do tema
                data.discountPercent = Math.round(100 - (100 * data.price / data.comparePrice));
            } else {
                // Se não houver desconto real, não forçamos mais um valor
                data.discountPercent = 0;
                data.comparePrice = 0;
            }

            console.log(`[PriceManager] ✅ Final price data for variant ${value}:`, {
                price: data.price,
                comparePrice: data.comparePrice,
                installment: data.price / CONFIG.installmentCount,
                pixPrice: data.price * (1 - CONFIG.pixDiscountRate)
            });

            console.log('[PriceManager] 📊 Dados finais da variante:', data);
            return data;
        }

        generateUpdatedHTML(template, data, isProductPage = false) {
            const formattedPrice = Utils.formatMoney(data.price);
            const formattedPrice2 = Utils.formatMoney(data.price * 1.1);
            const comparePrice = data.comparePrice;
            const formattedComparePrice = Utils.formatMoney(comparePrice);
            // Usa sempre o preço atual para o parcelamento
            const valorParcela = data.price / CONFIG.maxInterestFreeInstallments;
            const formattedParcela = Utils.formatMoney(valorParcela);

            // HTML base para páginas de produto (internas) e cards da home com estrutura completa
            if (isProductPage) {
    const pixPrice = Utils.formatMoney(data.price * (1 - CONFIG.pixDiscountRate));
    const installmentValue = data.price / CONFIG.maxInterestFreeInstallments;
    const formattedInstallment = Utils.formatMoney(installmentValue);

    return `
        <div class="price-list price-list--large" style="text-align: left; margin-bottom: 15px;">

            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px; white-space: nowrap; flex-wrap: wrap;">

                <div class="price price--compare" style="
                    color: #8c8c8c;
                    font-size: 16px;
                    text-decoration: line-through;
                    white-space: nowrap;
                    flex: 0 0 auto;
                ">
                    ${data.comparePrice > data.price ? formattedComparePrice : ''}
                </div>

                <div class="price price--highlight" style="
                    font-size: 20px;
                    font-weight: 700;
                    color: #111;
                    line-height: 1.1;
                    white-space: nowrap;
                    flex: 0 0 auto;
                ">
                    ${formattedPrice}
                </div>

            </div>

            <div class="price__pix-container" style="background-color: ${CONFIG.pixColor}; color: white; padding: 8px 12px; border-radius: 4px; margin-bottom: 10px;">
                <div style="font-size: 12px; font-weight: bold; margin-bottom: 3px;">
                    ${Math.round(CONFIG.pixDiscountRate * 100)}% de desconto no Pix
                </div>
                <div style="font-size: 18px; font-weight: bold; display: flex; align-items: center; gap: 6px;">
                    <svg width="14px" height="14px" fill="#ffffff" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.917 11.71a2.046 2.046 0 0 1-1.454-.602l-2.1-2.1a.4.4 0 0 0-.551 0l-2.108 2.108a2.044 2.044 0 0 1-1.454.602h-.414l2.66 2.66c.83.83 2.177.83 3.007 0l2.667-2.668h-.253zM4.25 4.282c.55 0 1.066.214 1.454.602l2.108 2.108a.39.39 0 0 0 .552 0l2.1-2.1a2.044 2.044 0 0 1 1.453-.602h.253L9.503 1.623a2.127 2.127 0 0 0-3.007 0l-2.66 2.66h.414z"></path>
                        <path d="m14.377 6.496-1.612-1.612a.307.307 0 0 1-.114.023h-.733c-.379 0-.75.154-1.017.422l-2.1 2.1a1.005 1.005 0 0 1-1.425 0L5.268 5.32a1.448 1.448 0 0 0-1.018-.422h-.9a.306.306 0 0 1-.109-.021L1.623 6.496c-.83.83-.83 2.177 0 3.008l1.618 1.618a.305.305 0 0 1 .108-.022h.9c.38 0 .75-.154 1.018-.422l2.108-2.108a.39.39 0 0 1 .551 0l2.1 2.1c.268.268.639.422 1.018.422h.733a.3.3 0 0 1 .114.023l1.612-1.612c.83-.83.83-2.177 0-3.008z"></path>
                    </svg>
                    <span>${pixPrice}</span>
                </div>
            </div>

            <div class="parcelamento" style="margin-top: 5px; line-height: 1.2;">
                <div style="font-size: 13px; color: #666;">Ou em até</div>
                <div style="font-size: 16px; font-weight: bold; color: #111;">
                    ${CONFIG.maxInterestFreeInstallments}x de ${formattedInstallment}
                </div>
                <div style="font-size: 13px; color: #666;">sem juros no cartão</div>
            </div>

        </div>
    `;
} else {
    return `
        <div class="price-list" style="text-align: left;">

            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px; white-space: nowrap;">

    <div class="price price--compare" style="
        color: #8c8c8c;
        font-size: 14px;
        text-decoration: line-through;
        white-space: nowrap;
        flex: 0 0 auto;
    ">
        ${data.comparePrice > data.price ? formattedComparePrice : ''}
    </div>

    <div class="price price--highlight" style="
        font-size: 18px;
        font-weight: 700;
        color: #111;
        line-height: 1.2;
        white-space: nowrap;
        flex: 0 0 auto;
    ">
        ${formattedPrice}
    </div>

</div>

            <div class="price__pix-container" style="background-color: ${CONFIG.pixColor}; color: white; padding: 6px 10px; border-radius: 4px; margin-bottom: 8px; display: inline-block; width: 100%;">
                <div style="font-size: 11px; font-weight: bold; line-height: 1.1;">
                    ${Math.round(CONFIG.pixDiscountRate * 100)}% de desconto no Pix
                </div>
                <div style="font-size: 15px; font-weight: bold; line-height: 1.1;">
                    ❖ ${Utils.formatMoney(data.price * (1 - CONFIG.pixDiscountRate))}
                </div>
            </div>

            <div class="parcelamento" style="font-size: 12px; color: #666;">
                ou ${CONFIG.maxInterestFreeInstallments}x de
                <span style="color: #19a35b;">${formattedParcela}</span>
            </div>
        </div>
    `;
}

            // HTML para cards na home page (aplicando a mesma estrutura de layout do produto)
            const pixPrice = Utils.formatMoney(data.price * (1 - CONFIG.pixDiscountRate));
            const installmentValue = data.price / CONFIG.installmentCount;
            const formattedInstallment = Utils.formatMoney(installmentValue);

            return `
                <span class="price price--highlight"><span>${formattedPrice}</span></span>

                
                <div class="price__pix-container" style="background-color: ${CONFIG.pixColor}; color: white; padding: 6px 10px; border-radius: 4px; margin: 8px 0;">
                    <div style="font-size: 11px; font-weight: bold; margin-bottom: 2px;">${Math.round(CONFIG.pixDiscountRate * 100)}% de desconto no Pix</div>
                    <div style="font-size: 16px; font-weight: bold;">
                    <svg width="12px" height="12px" fill="#ffffff" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M11.917 11.71a2.046 2.046 0 0 1-1.454-.602l-2.1-2.1a.4.4 0 0 0-.551 0l-2.108 2.108a2.044 2.044 0 0 1-1.454.602h-.414l2.66 2.66c.83.83 2.177.83 3.007 0l2.667-2.668h-.253zM4.25 4.282c.55 0 1.066.214 1.454.602l2.108 2.108a.39.39 0 0 0 .552 0l2.1-2.1a2.044 2.044 0 0 1 1.453-.602h.253L9.503 1.623a2.127 2.127 0 0 0-3.007 0l-2.66 2.66h.414z"></path><path d="m14.377 6.496-1.612-1.612a.307.307 0 0 1-.114.023h-.733c-.379 0-.75.154-1.017.422l-2.1 2.1a1.005 1.005 0 0 1-1.425 0L5.268 5.32a1.448 1.448 0 0 0-1.018-.422h-.9a.306.306 0 0 1-.109-.021L1.623 6.496c-.83.83-.83 2.177 0 3.008l1.618 1.618a.305.305 0 0 1 .108-.022h.901c.38 0 .75-.153 1.018-.421L7.375 8.57a1.034 1.034 0 0 1 1.426 0l2.1 2.1c.267.268.638.421 1.017.421h.733c.04 0 .079.01.114.024l1.612-1.612c.83-.83.83-2.178 0-3.008z"></path></g></svg>
                    ${pixPrice}
                    </div>
                </div>

                <p class="parcelamento" style="font-size:14px; margin-top: 3px; margin-bottom: 9px">ou <b>${CONFIG.installmentCount}x</b> de <span style="color: var(--product-on-sale-accent)"><b style="font-size:15px;">${formattedInstallment}</b></span></p>`;
        }

        setupDynamicContentObserver() {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const newCards = node.matches(CONFIG.selectors.productCards.join(',')) ? [node] : Utils.findElements(CONFIG.selectors.productCards, node);
                            newCards.forEach(card => this.setupProductCard(card, `dynamic-${Date.now()}`));
                        }
                    });
                });
            });
            observer.observe(document.body, { childList: true, subtree: true });
            this.observers.set('dynamic-content', observer);
        }
    }

    // Inicialização segura
    function initializeScript() {
        // Evita múltiplas inicializações
        if (!window.manusPriceManager) {
            window.manusPriceManager = new PriceManager();
            window.manusPriceManager.init();
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            initializeScript();
            removePriceTitle();
            addFlexStyles();
            scheduleAvailabilityTextFix();
        });
    } else {
        initializeScript();
        removePriceTitle();
        addFlexStyles();
        scheduleAvailabilityTextFix();
    }

    document.addEventListener('change', (e) => {
        const t = e.target;
        if (t && (t.name === 'id' || (t.tagName === 'SELECT' && /product-select|variant/i.test(t.id || '')))) {
            scheduleAvailabilityTextFix();
        }
    }, true);

    function removePriceTitle() {
        // Remove apenas o título "Preço"
        const priceTitle = document.querySelector('.product-form__info-title:not(.text--subdued)');
        if (priceTitle && priceTitle.textContent.includes('Preço')) {
            priceTitle.style.setProperty('display', 'none', 'important');
            priceTitle.remove();
        }
    }

    // Adiciona CSS para garantir que product-form__info-item tenha display flex
    function addFlexStyles() {
        const style = document.createElement('style');
        style.textContent = `
            .product-form__info-item {
                display: flex !important;
                align-items: center !important;
            }
            
            /* Estilos para a estrutura de produto da home */
            .product-item__price-list {
                display: block !important;
                visibility: visible !important;
                opacity: 1 !important;
            }
            
            .price__pix-container {
                background-color: ${CONFIG.pixColor} !important;
                color: white !important;
                padding: 6px 10px !important;
                border-radius: 4px !important;
                margin: 8px 0 !important;
            }
            
            .price--highlight {
                font-weight: bold !important;
                color: #000000ff !important;
            }
            
            .price--compare {
                text-decoration: line-through !important;
                color: #999 !important;
            }
            
            .parcelamento {
                font-size: 14px !important;
                margin-top: 3px !important;
                margin-bottom: 9px !important;
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Corrige vazamento de hash de pluralização (Ruby/Liquid) na linha "Disponibilidade",
     * ex.: {"one"=>"Em estoque ({{count}} unidade)... — não altera outros textos.
     */
    function getVariantInventoryInfoForForm(form) {
        if (!form) return null;
        const hidden = form.querySelector('input[name="id"]') || document.querySelector('input[name="id"]');
        const vid = hidden && hidden.value;
        if (!vid) return null;
        const roots = [form, document.querySelector('.shopify-section--main-product'), document];
        for (const root of roots) {
            if (!root || !root.querySelectorAll) continue;
            const scripts = root.querySelectorAll('script[type="application/json"]');
            for (const s of scripts) {
                try {
                    const d = JSON.parse(s.textContent);
                    const variants = d.variants || (d.product && d.product.variants);
                    if (!variants || !Array.isArray(variants)) continue;
                    const v = variants.find(x => String(x.id) === String(vid));
                    if (!v) continue;
                    const q = v.inventory_quantity;
                    return {
                        qty: typeof q === 'number' ? q : null,
                        available: v.available !== false && v.available !== 'false'
                    };
                } catch (e) { /* ignore */ }
            }
        }
        try {
            const variants = window.meta && window.meta.product && window.meta.product.variants;
            if (variants && Array.isArray(variants)) {
                const v = variants.find(x => String(x.id) === String(vid));
                if (v) {
                    const q = v.inventory_quantity;
                    return {
                        qty: typeof q === 'number' ? q : null,
                        available: v.available !== false
                    };
                }
            }
        } catch (e) { /* ignore */ }
        return null;
    }

    function formatAvailabilityMessage(info) {
        if (!info) {
            return 'Em estoque, pronto para ser enviado';
        }
        if (info.available === false) {
            return 'Indisponível';
        }
        if (info.qty === null || info.qty === undefined) {
            return 'Em estoque, pronto para ser enviado';
        }
        if (info.qty <= 0) {
            return 'Indisponível';
        }
        if (info.qty === 1) {
            return 'Em estoque (1 unidade), pronto para ser enviado';
        }
        return `Em estoque (${info.qty} unidades), pronto para ser enviado`;
    }

    function textLooksLikeBrokenPluralHash(t) {
        if (!t || typeof t !== 'string') return false;
        if (t.includes('"one"=>')) return true;
        if (t.includes("'one'=>")) return true;
        return t.includes('{{count}}') && t.includes('Em estoque') && t.includes('pronto para ser enviado');
    }

    function replaceBrokenPluralHashInString(t, msg) {
        if (!textLooksLikeBrokenPluralHash(t)) return t;
        const start = t.indexOf('{');
        const end = t.lastIndexOf('}');
        if (start === -1 || end <= start || (t.indexOf('"one"=>') === -1 && t.indexOf("'one'=>") === -1)) {
            return t;
        }
        return (t.slice(0, start) + msg + t.slice(end + 1)).trim();
    }

    function fixBrokenAvailabilityPluralLeak() {
        try {
            const form = document.querySelector('.product-form, form[action*="/cart/add"]');
            if (!form) return;
            const info = getVariantInventoryInfoForForm(form);
            const msg = formatAvailabilityMessage(info);

            const walk = (node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    const t = node.textContent;
                    if (!textLooksLikeBrokenPluralHash(t)) return;
                    const next = replaceBrokenPluralHashInString(t, msg);
                    if (next !== t) {
                        node.textContent = next;
                    }
                    return;
                }
                if (node.nodeType === Node.ELEMENT_NODE) {
                    for (let i = 0; i < node.childNodes.length; i++) {
                        walk(node.childNodes[i]);
                    }
                }
            };
            walk(form);
        } catch (e) {
            console.warn('[PriceManager] fixBrokenAvailabilityPluralLeak:', e);
        }
    }

    function scheduleAvailabilityTextFix() {
        fixBrokenAvailabilityPluralLeak();
        setTimeout(fixBrokenAvailabilityPluralLeak, 200);
        setTimeout(fixBrokenAvailabilityPluralLeak, 800);
    }

    // Função para processar estrutura HTML específica do produto
    function processProductItemHTML(htmlString) {
        // Extrai preços do HTML original - busca por diferentes padrões
        const pricePatterns = [
            /R\$\s*([\d.,]+)/g,
            /price--highlight[^>]*>.*?R\$\s*([\d.,]+)/g,
            /data-money-convertible[^>]*>.*?R\$\s*([\d.,]+)/g
        ];

        let priceMatch = null;
        for (const pattern of pricePatterns) {
            priceMatch = htmlString.match(pattern);
            if (priceMatch && priceMatch.length > 0) {
                break;
            }
        }

        if (!priceMatch || priceMatch.length === 0) {
            console.warn('⚠️ Nenhum preço encontrado no HTML');
            return htmlString;
        }

        // Extrai o preço principal (primeiro preço encontrado)
        const currentPrice = Utils.extractPrice(priceMatch[0]);
        const comparePrice = priceMatch.length > 1 ? Utils.extractPrice(priceMatch[1]) : 0;

        // Valida se os preços foram extraídos corretamente
        if (currentPrice <= 0) {
            console.warn('⚠️ Preço atual inválido extraído:', currentPrice);
            return htmlString;
        }

        console.log('🔍 Preços extraídos:', { currentPrice, comparePrice });

        // Calcula valores derivados
        const pixPrice = currentPrice * (1 - CONFIG.pixDiscountRate);
        const installmentValue = currentPrice / CONFIG.installmentCount;
        const discountPercent = comparePrice > currentPrice ? Math.round(100 - (100 * currentPrice / comparePrice)) : 0;

        console.log('🔍 Valores calculados:', { pixPrice, installmentValue, discountPercent });

        // Formata valores
        const formattedPrice = Utils.formatMoney(currentPrice);
        const formattedComparePrice = Utils.formatMoney(comparePrice);
        const formattedPixPrice = Utils.formatMoney(pixPrice);
        const formattedInstallment = Utils.formatMoney(installmentValue);

        // Cria o HTML atualizado com o estilo completo
        let updatedHTML = htmlString;

        // Atualiza o label de desconto PIX se existir
        const pixDiscountPercent = Math.round(CONFIG.pixDiscountRate * 100);
        updatedHTML = updatedHTML.replace(
            /<span class="product-label product-label--on-sale">[\s\S]*?<\/span>/g,
            `<span class="product-label product-label--on-sale"><svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-arrow-down" width="1.3em" height="1.3em" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round" style="margin-right: -3px; margin-left: -3px;">
  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
  <line x1="12" y1="5" x2="12" y2="19"></line>
  <line x1="18" y1="13" x2="12" y2="19"></line>
  <line x1="6" y1="13" x2="12" y2="19"></line>
          </svg> ${pixDiscountPercent}%</span>`
        );

        // Substitui todo o conteúdo da div product-item__price-list
        const newPriceListContent = `
                <div class="price-list price-list--large" style="text-align: left; margin-bottom: 15px;">
                    
                    <div class="price__pix-container" style="background-color: ${CONFIG.pixColor}; color: white; padding: 8px 12px; border-radius: 4px; margin-bottom: 10px;">
                        <div style="font-size: 12px; font-weight: bold; margin-bottom: 3px;">${pixDiscountPercent}% de desconto no Pix</div>
                        <div style="font-size: 18px; font-weight: bold;">
                        <svg width="14px" height="14px" fill="#ffffff" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M11.917 11.71a2.046 2.046 0 0 1-1.454-.602l-2.1-2.1a.4.4 0 0 0-.551 0l-2.108 2.108a2.044 2.044 0 0 1-1.454.602h-.414l2.66 2.66c.83.83 2.177.83 3.007 0l2.667-2.668h-.253zM4.25 4.282c.55 0 1.066.214 1.454.602l2.108 2.108a.39.39 0 0 0 .552 0l2.1-2.1a2.044 2.044 0 0 1 1.453-.602h.253L9.503 1.623a2.127 2.127 0 0 0-3.007 0l-2.66 2.66h.414z"></path><path d="m14.377 6.496-1.612-1.612a.307.307 0 0 1-.114.023h-.733c-.379 0-.75.154-1.017.422l-2.1 2.1a1.005 1.005 0 0 1-1.425 0L5.268 5.32a1.448 1.448 0 0 0-1.018-.422h-.9a.306.306 0 0 1-.109-.021L1.623 6.496c-.83.83-.83 2.177 0 3.008l1.618 1.618a.305.305 0 0 1 .108-.022h.901c.38 0 .75-.153 1.018-.421L7.375 8.57a1.034 1.034 0 0 1 1.426 0l2.1 2.1c.267.268.638.421 1.017.421h.733c.04 0 .079.01.114.024l1.612-1.612c.83-.83.83-2.178 0-3.008z"></path></g></svg>
                        ${formattedPixPrice}
                        </div>
                    </div>

                    <div class="price__current-container" style="margin-bottom: 10px;">
                        ${comparePrice > 0 ? `<span class="price price--highlight" style="text-decoration: line-through;font-size: 16px; font-weight: bold; color: #000000ff;" data-money-convertible="">${formattedComparePrice}</span>` : ''}
                        <span class="price price--highlight" style="font-size: 14px; font-weight: bold; color: #000000ff;" data-money-convertible="">${formattedPrice}</span>
                    </div>

                    <div class="price__installments-container" style="padding: 8px 12px; border-radius: 4px; margin-bottom: 10px;">
                        <div style="color: #666; font-size: 12px;">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Ou em até</div>
                        <span style="font-size: 14px; color: #333; font-weight: bold;">
                         <svg width="14px" height="14px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <rect width="24" height="24" fill="white"></rect> <path fill-rule="evenodd" clip-rule="evenodd" d="M2 11H22L22 15.0658C22.0001 15.9523 22.0001 16.7161 21.9179 17.3278C21.8297 17.9833 21.631 18.6117 21.1213 19.1213C20.6117 19.631 19.9833 19.8297 19.3278 19.9179C18.7161 20.0001 17.9523 20.0001 17.0658 20H6.93414C6.04767 20.0001 5.28387 20.0001 4.67221 19.9179C4.0167 19.8297 3.38835 19.631 2.87869 19.1213C2.36902 18.6117 2.17028 17.9833 2.08215 17.3278C1.99991 16.7161 1.99995 15.9523 2 15.0658L2 11ZM5 14C5 13.4477 5.44772 13 6 13H8C8.55228 13 9 13.4477 9 14C9 14.5523 8.55228 15 8 15H6C5.44772 15 5 14.5523 5 14ZM10 14C10 13.4477 10.4477 13 11 13H13C13.5523 13 14 13.4477 14 14C14 14.5523 13.5523 15 13 15H11C10.4477 15 10 14.5523 10 14Z" fill="#323232"></path> <path d="M22 9.00001L22 8.93418C22.0001 8.04769 22.0001 7.28387 21.9179 6.67221C21.8297 6.0167 21.631 5.38835 21.1213 4.87869C20.6117 4.36902 19.9833 4.17028 19.3278 4.08215C18.7161 3.99991 17.9523 3.99995 17.0658 4L6.93418 4C6.04769 3.99995 5.28388 3.99991 4.67222 4.08215C4.0167 4.17028 3.38835 4.36902 2.87869 4.87868C2.36902 5.38835 2.17028 6.0167 2.08215 6.67221C1.99991 7.28388 1.99995 8.0477 2 8.9342L2 9L22 9.00001Z" fill="#323232"></path> </g></svg>
                        ${CONFIG.installmentCount}x de ${formattedInstallment}</span>
                        <span style="color: #666; font-size: 12px;">
                        <BR>
                        sem juros no cartão</span>
                    </div>
                </div>`;

        // Substitui todo o conteúdo da div product-item__price-list
        updatedHTML = updatedHTML.replace(
            /<div class="product-item__price-list price-list">[\s\S]*?<\/div>/,
            `<div class="product-item__price-list price-list">${newPriceListContent}</div>`
        );

        console.log('✅ HTML do produto processado com sucesso - Estilo completo aplicado');
        return updatedHTML;
    }

    // Função específica para processar blocos de produto em páginas individuais
    function processIndividualProductBlocks() {
        if (!Utils.isProductPage()) {
            return;
        }

        // Verifica se é uma página com variantes funcionando
        const productForm = document.querySelector('.product-form');
        const variantSelect = document.querySelector('select[name="id"]');

        if (productForm && variantSelect) {
            return; // Não processa em páginas com variantes funcionando
        }

        console.log('🛍️ Processando blocos de produto em página individual');

        // Procura por todos os blocos .product-item na página
        const productBlocks = document.querySelectorAll('.product-item');

        productBlocks.forEach((block, index) => {
            const priceList = block.querySelector('.product-item__price-list');
            if (priceList && !priceList.hasAttribute('data-processed')) {
                // Extrai dados do preço atual
                const currentPriceElement = priceList.querySelector('.price--highlight span, .price--highlight');
                const comparePriceElement = priceList.querySelector('.price--compare');

                if (currentPriceElement) {
                    const currentPrice = Utils.extractPrice(currentPriceElement.textContent);
                    const comparePrice = comparePriceElement ? Utils.extractPrice(comparePriceElement.textContent) : 0;

                    const data = {
                        price: currentPrice,
                        comparePrice: comparePrice,
                        variantId: `individual-block-${index}`,
                        discountPercent: comparePrice > currentPrice ? Math.round(100 - (100 * currentPrice / comparePrice)) : 0
                    };

                    // Aplica o estilo completo da home para blocos individuais
                    const updatedHTML = window.manusPriceManager.generateUpdatedHTML('', data, true);
                    priceList.innerHTML = updatedHTML;
                    priceList.setAttribute('data-processed', 'true');
                    console.log(`✅ Bloco de produto individual ${index + 1} processado com layout completo da home`);
                }
            }
        });

        // Processa também seções de recomendações e produtos vistos recentemente
        processRecommendationsSection();
        processRecentlyViewedSection();
    }

    // Função para processar seção de recomendações
    function processRecommendationsSection() {
        const recommendationsContainer = document.querySelector('.product-recommendations');
        if (recommendationsContainer && !recommendationsContainer.hasAttribute('data-processed')) {
            console.log('🛍️ Processando seção de recomendações');

            // Procura por produtos na seção de recomendações
            const recommendedProducts = recommendationsContainer.querySelectorAll('.product-item, .product-card, .grid-product, [class*="product"]');

            recommendedProducts.forEach((product, index) => {
                const priceList = product.querySelector('.product-item__price-list, .price-list, [class*="price"]');
                if (priceList && !priceList.hasAttribute('data-processed')) {
                    // Extrai dados do preço atual
                    const currentPriceElement = priceList.querySelector('.price--highlight span, .price--highlight');
                    const comparePriceElement = priceList.querySelector('.price--compare');

                    if (currentPriceElement) {
                        const currentPrice = Utils.extractPrice(currentPriceElement.textContent);
                        const comparePrice = comparePriceElement ? Utils.extractPrice(comparePriceElement.textContent) : 0;

                        const data = {
                            price: currentPrice,
                            comparePrice: comparePrice,
                            variantId: `recommendation-${index}`,
                            discountPercent: comparePrice > currentPrice ? Math.round(100 - (100 * currentPrice / comparePrice)) : 0
                        };

                        // Aplica o estilo completo da home para produtos recomendados
                        const updatedHTML = window.manusPriceManager.generateUpdatedHTML('', data, true);
                        priceList.innerHTML = updatedHTML;
                        priceList.setAttribute('data-processed', 'true');
                        console.log(`✅ Produto recomendado ${index + 1} processado com layout completo da home`);
                    }
                }
            });

            recommendationsContainer.setAttribute('data-processed', 'true');
        }
    }

    // Função para processar seção de produtos vistos recentemente
    function processRecentlyViewedSection() {
        const recentlyViewedContainer = document.querySelector('.recently-viewed-products-placeholder');
        if (recentlyViewedContainer && !recentlyViewedContainer.hasAttribute('data-processed')) {
            console.log('🛍️ Processando seção de produtos vistos recentemente');

            // Procura por produtos na seção de produtos vistos recentemente
            const recentlyViewedProducts = recentlyViewedContainer.querySelectorAll('.product-item, .product-card, .grid-product, [class*="product"]');

            recentlyViewedProducts.forEach((product, index) => {
                const priceList = product.querySelector('.product-item__price-list, .price-list, [class*="price"]');
                if (priceList && !priceList.hasAttribute('data-processed')) {
                    // Extrai dados do preço atual
                    const currentPriceElement = priceList.querySelector('.price--highlight span, .price--highlight');
                    const comparePriceElement = priceList.querySelector('.price--compare');

                    if (currentPriceElement) {
                        const currentPrice = Utils.extractPrice(currentPriceElement.textContent);
                        const comparePrice = comparePriceElement ? Utils.extractPrice(comparePriceElement.textContent) : 0;

                        const data = {
                            price: currentPrice,
                            comparePrice: comparePrice,
                            variantId: `recently-viewed-${index}`,
                            discountPercent: comparePrice > currentPrice ? Math.round(100 - (100 * currentPrice / comparePrice)) : 0
                        };

                        // Aplica o estilo completo da home para produtos vistos recentemente
                        const updatedHTML = window.manusPriceManager.generateUpdatedHTML('', data, true);
                        priceList.innerHTML = updatedHTML;
                        priceList.setAttribute('data-processed', 'true');
                        console.log(`✅ Produto visto recentemente ${index + 1} processado com layout completo da home`);
                    }
                }
            });

            recentlyViewedContainer.setAttribute('data-processed', 'true');
        }
    }

    // Expõe a função para uso global
    window.processProductItemHTML = processProductItemHTML;
    window.processIndividualProductBlocks = processIndividualProductBlocks;
    window.processRecommendationsSection = processRecommendationsSection;
    window.processRecentlyViewedSection = processRecentlyViewedSection;

    // Função para atualizar labels de desconto PIX
    function updatePixDiscountLabels() {
        const discountLabels = document.querySelectorAll('.product-label.product-label--on-sale');

        discountLabels.forEach(label => {
            // Verifica se o label já foi atualizado
            if (!label.hasAttribute('data-pix-updated')) {
                // Encontra o produto relacionado ao label
                const productItem = label.closest('.product-item');
                if (productItem) {
                    // Busca os preços no container de preço do produto
                    const priceList = productItem.querySelector('.product-item__price-list');
                    if (priceList) {
                        // Busca o preço comparado (riscado) - deve ser 3.173
                        const comparePriceElement = priceList.querySelector('.price--highlight[style*="line-through"]');
                        // Busca o preço do PIX (com desconto) - deve ser 2.582
                        const pixPriceContainer = priceList.querySelector('.price__pix-container');

                        if (comparePriceElement && pixPriceContainer) {
                            const comparePrice = Utils.extractPrice(comparePriceElement.textContent); // 3.173

                            // Busca o elemento específico que contém o valor do preço PIX (dentro da div com font-size 18px)
                            const pixPriceValueElement = pixPriceContainer.querySelector('div[style*="font-size: 18px"], div[style*="font-size:18px"]');
                            let pixPrice = 0;

                            if (pixPriceValueElement) {
                                // Extrai o preço do elemento específico - busca por padrão R$ seguido de números
                                const priceMatch = pixPriceValueElement.textContent.match(/R\$\s*([\d.,]+)/);
                                if (priceMatch && priceMatch[1]) {
                                    pixPrice = Utils.extractPrice(priceMatch[1]);
                                } else {
                                    // Se não encontrar com R$, tenta extrair diretamente
                                    pixPrice = Utils.extractPrice(pixPriceValueElement.textContent);
                                }
                            } else {
                                // Fallback: busca todos os preços no container e pega o maior (que deve ser o preço do PIX)
                                const allPrices = pixPriceContainer.textContent.match(/R\$\s*[\d.,]+/g);
                                if (allPrices && allPrices.length > 0) {
                                    // Extrai todos os valores e pega o maior (que geralmente é o preço principal do PIX)
                                    const prices = allPrices.map(p => Utils.extractPrice(p));
                                    pixPrice = Math.max(...prices);
                                }
                            }

                            if (comparePrice > 0 && pixPrice > 0 && comparePrice > pixPrice) {
                                // Calcula a porcentagem: (comparePrice - pixPrice) / comparePrice * 100
                                // Exemplo: (3173 - 2582) / 3173 * 100 = 591 / 3173 * 100 = 18%
                                const discountPercent = Math.round(((comparePrice - pixPrice) / comparePrice) * 100);

                                const svgIcon = label.querySelector('svg');
                                if (svgIcon) {
                                    // Mantém o SVG e atualiza apenas o texto
                                    const textNode = label.childNodes[1]; // O texto após o SVG
                                    if (textNode && textNode.nodeType === Node.TEXT_NODE) {
                                        textNode.textContent = ` ${discountPercent}%`;
                                    } else {
                                        // Se não encontrar o texto, adiciona após o SVG
                                        label.appendChild(document.createTextNode(` ${discountPercent}%`));
                                    }
                                    label.setAttribute('data-pix-updated', 'true');
                                    console.log(`✅ Label de desconto atualizado: ${comparePrice} - ${pixPrice} = ${discountPercent}% (diferença: ${comparePrice - pixPrice})`);
                                }
                            }
                        }
                    }
                }
            }
        });
    }

    // Expõe a função para uso global
    window.updatePixDiscountLabels = updatePixDiscountLabels;

    // Função para forçar aplicação em todos os elementos da home
    function forceApplyToHomeProducts() {
        const productItems = document.querySelectorAll('.product-item');
        console.log(`🔄 Forçando aplicação em ${productItems.length} produtos da home`);

        productItems.forEach((item, index) => {
            const priceList = item.querySelector('.product-item__price-list');
            if (priceList && !priceList.hasAttribute('data-processed')) {
                const itemHTML = item.outerHTML;
                const updatedHTML = processProductItemHTML(itemHTML);

                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = updatedHTML;
                const updatedPriceList = tempDiv.querySelector('.product-item__price-list');

                if (updatedPriceList) {
                    priceList.innerHTML = updatedPriceList.innerHTML;
                    priceList.setAttribute('data-processed', 'true');
                    console.log(`✅ Produto ${index + 1} processado com sucesso`);
                }
            }
        });
    }

    // Expõe a função para uso global
    window.forceApplyToHomeProducts = forceApplyToHomeProducts;

    // Função genérica para aplicar o layout completo da home aos produtos de qualquer coleção
    function forceApplyToCollectionProducts() {
        const productItems = document.querySelectorAll('.product-item');
        const currentPath = window.location.pathname;
        const collectionName = currentPath.split('/collections/')[1]?.split('/')[0] || 'collection';

        console.log(`🔄 Forçando aplicação do layout completo da home em ${productItems.length} produtos da coleção: ${collectionName}`);

        productItems.forEach((item, index) => {
            const priceList = item.querySelector('.product-item__price-list');
            if (priceList && !priceList.hasAttribute('data-processed')) {
                // Extrai dados do preço atual
                const currentPriceElement = priceList.querySelector('.price--highlight span, .price--highlight');
                const comparePriceElement = priceList.querySelector('.price--compare');

                if (currentPriceElement) {
                    const currentPrice = Utils.extractPrice(currentPriceElement.textContent);
                    const comparePrice = comparePriceElement ? Utils.extractPrice(comparePriceElement.textContent) : 0;

                    const data = {
                        price: currentPrice,
                        comparePrice: comparePrice,
                        variantId: `${collectionName}-${index}`,
                        discountPercent: comparePrice > currentPrice ? Math.round(100 - (100 * currentPrice / comparePrice)) : 0
                    };

                    // Aplica o estilo completo da home (igual ao segundo grid da home)
                    const updatedHTML = window.manusPriceManager.generateUpdatedHTML('', data, false);
                    priceList.innerHTML = updatedHTML;
                    priceList.setAttribute('data-processed', 'true');
                    console.log(`✅ Produto ${collectionName} ${index + 1} processado com layout completo da home`);
                }
            }
        });
    }

    // Função específica para aplicar o layout completo nas páginas de produto individual
    function forceApplyToIndividualProductPage() {
        // Verifica se é uma página com variantes funcionando
        const productForm = document.querySelector('.product-form');
        const variantSelect = document.querySelector('select[name="id"]');

        if (productForm && variantSelect) {
            console.log('🛍️ Página com variantes funcionando - Não aplicando layout da home');
            return; // Não aplica em páginas com variantes funcionando
        }

        const productItems = document.querySelectorAll('.product-item');
        const currentPath = window.location.pathname;
        const productName = currentPath.split('/products/')[1]?.split('/')[0] || 'product';

        console.log(`🛍️ Forçando aplicação do layout completo da home em ${productItems.length} produtos da página individual: ${productName}`);

        productItems.forEach((item, index) => {
            const priceList = item.querySelector('.product-item__price-list');
            if (priceList && !priceList.hasAttribute('data-processed')) {
                // Extrai dados do preço atual
                const currentPriceElement = priceList.querySelector('.price--highlight span, .price--highlight');
                const comparePriceElement = priceList.querySelector('.price--compare');

                if (currentPriceElement) {
                    const currentPrice = Utils.extractPrice(currentPriceElement.textContent);
                    const comparePrice = comparePriceElement ? Utils.extractPrice(comparePriceElement.textContent) : 0;

                    const data = {
                        price: currentPrice,
                        comparePrice: comparePrice,
                        variantId: `${productName}-individual-${index}`,
                        discountPercent: comparePrice > currentPrice ? Math.round(100 - (100 * currentPrice / comparePrice)) : 0
                    };

                    // Aplica o estilo completo da home para produtos individuais
                    const updatedHTML = window.manusPriceManager.generateUpdatedHTML('', data, true);
                    priceList.innerHTML = updatedHTML;
                    priceList.setAttribute('data-processed', 'true');
                    console.log(`✅ Produto individual ${productName} ${index + 1} processado com layout completo da home`);
                }
            }
        });

        // Se não encontrar .product-item, tenta containers de preço diretos
        if (productItems.length === 0) {
            const priceContainers = Utils.findElements(CONFIG.selectors.priceContainer);
            if (priceContainers.length > 0) {
                console.log(`🛍️ Encontrados ${priceContainers.length} containers de preço diretos na página individual: ${productName}`);

                priceContainers.forEach((container, index) => {
                    if (!container.hasAttribute('data-processed')) {
                        // Extrai dados do preço atual
                        const currentPriceElement = container.querySelector('.price--highlight span, .price--highlight');
                        const comparePriceElement = container.querySelector('.price--compare');

                        if (currentPriceElement) {
                            const currentPrice = Utils.extractPrice(currentPriceElement.textContent);
                            const comparePrice = comparePriceElement ? Utils.extractPrice(comparePriceElement.textContent) : 0;

                            const data = {
                                price: currentPrice,
                                comparePrice: comparePrice,
                                variantId: `${productName}-container-${index}`,
                                discountPercent: comparePrice > currentPrice ? Math.round(100 - (100 * currentPrice / comparePrice)) : 0
                            };

                            // Aplica o estilo completo da home para produtos individuais
                            const updatedHTML = window.manusPriceManager.generateUpdatedHTML('', data, true);
                            container.innerHTML = updatedHTML;
                            container.setAttribute('data-processed', 'true');
                            console.log(`✅ Container de preço individual ${productName} ${index + 1} processado com layout completo da home`);
                        }
                    }
                });
            }
        }
    }

    // Expõe a função para uso global
    window.forceApplyToCollectionProducts = forceApplyToCollectionProducts;
    window.forceApplyToIndividualProductPage = forceApplyToIndividualProductPage;

    // Aplica automaticamente após um delay para garantir que todos os elementos estejam carregados
    setTimeout(() => {
        if (Utils.isHomePage()) {
            forceApplyToHomeProducts();
        }
        else if (Utils.isCollectionsPage()) {
            // Detecta automaticamente qualquer coleção e aplica o layout completo da home
            const currentPath = window.location.pathname;
            const isCollectionPage = currentPath.includes('/collections/');

            if (isCollectionPage) {
                const collectionName = currentPath.split('/collections/')[1]?.split('/')[0] || 'collection';
                console.log(`🔥 Coleção detectada: ${collectionName} - Aplicando layout completo da home`);
                forceApplyToCollectionProducts();
            } else {
                console.log('🔄 Forçando aplicação dos produtos na collections');
                const productItems = document.querySelectorAll('.product-item');
                productItems.forEach((item, index) => {
                    const priceList = item.querySelector('.product-item__price-list');
                    if (priceList && !priceList.hasAttribute('data-processed')) {
                        // Extrai dados do preço atual
                        const currentPriceElement = priceList.querySelector('.price--highlight span, .price--highlight');
                        const comparePriceElement = priceList.querySelector('.price--compare');

                        if (currentPriceElement) {
                            const currentPrice = Utils.extractPrice(currentPriceElement.textContent);
                            const comparePrice = comparePriceElement ? Utils.extractPrice(comparePriceElement.textContent) : 0;

                            const data = {
                                price: currentPrice,
                                comparePrice: comparePrice,
                                variantId: `collections-auto-${index}`,
                                discountPercent: comparePrice > currentPrice ? Math.round(100 - (100 * currentPrice / comparePrice)) : 0
                            };

                            // Aplica o estilo completo da home para collections (igual ao segundo grid da home)
                            const updatedHTML = window.manusPriceManager.generateUpdatedHTML('', data, true);
                            priceList.innerHTML = updatedHTML;
                            priceList.setAttribute('data-processed', 'true');
                            console.log(`✅ Produto collections ${index + 1} processado com sucesso - Estilo completo aplicado`);
                        }
                    }
                });
            }
        }
        else if (Utils.isProductPage()) {
            // Verifica se é uma página de produto com variantes funcionando
            const productForm = document.querySelector('.product-form');
            const variantSelect = document.querySelector('select[name="id"]');

            if (productForm && variantSelect) {
                console.log('🛍️ Página de produto com variantes detectada - Mantendo funcionalidade original');
                // Não aplica o layout da home em páginas com variantes funcionando
            } else {
                // Detecta automaticamente páginas de produto individual e aplica o layout completo da home
                const currentPath = window.location.pathname;
                const productName = currentPath.split('/products/')[1]?.split('/')[0] || 'product';
                console.log(`🛍️ Página de produto individual detectada: ${productName} - Aplicando layout completo da home`);
                forceApplyToIndividualProductPage();
                processIndividualProductBlocks();
            }
        }

        // Atualiza os labels de desconto PIX
        updatePixDiscountLabels();
    }, 2000);

    // Processamento periódico para seções carregadas dinamicamente
    if (Utils.isProductPage()) {
        const productForm = document.querySelector('.product-form');
        const variantSelect = document.querySelector('select[name="id"]');

        if (!productForm || !variantSelect) {
            // Processa seções dinâmicas a cada 3 segundos por 30 segundos
            let attempts = 0;
            const maxAttempts = 10;

            const processDynamicSections = setInterval(() => {
                attempts++;
                console.log(`🔄 Tentativa ${attempts}/${maxAttempts} - Processando seções dinâmicas`);

                processRecommendationsSection();
                processRecentlyViewedSection();

                if (attempts >= maxAttempts) {
                    clearInterval(processDynamicSections);
                    console.log('✅ Processamento periódico de seções dinâmicas finalizado');
                }
            }, 3000);
        }
    }

    // Adiciona um observador para atualizar labels de desconto quando novos elementos são adicionados
    const discountObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    const newLabels = node.matches('.product-label.product-label--on-sale') ? [node] : node.querySelectorAll('.product-label.product-label--on-sale');
                    if (newLabels.length > 0) {
                        setTimeout(updatePixDiscountLabels, 100);
                    }

                    // Detecta novos blocos de produto em páginas individuais
                    if (Utils.isProductPage()) {
                        const productForm = document.querySelector('.product-form');
                        const variantSelect = document.querySelector('select[name="id"]');

                        if (!productForm || !variantSelect) {
                            const newProductBlocks = node.matches('.product-item') ? [node] : node.querySelectorAll('.product-item');
                            if (newProductBlocks.length > 0) {
                                setTimeout(processIndividualProductBlocks, 100);
                            }
                        }
                    }

                    // Detecta quando seções de recomendações são carregadas
                    if (node.matches('.product-recommendations') || node.querySelector('.product-recommendations')) {
                        console.log('🛍️ Seção de recomendações detectada - Processando produtos');
                        setTimeout(processRecommendationsSection, 500);
                    }

                    // Detecta quando seções de produtos vistos recentemente são carregadas
                    if (node.matches('.recently-viewed-products-placeholder') || node.querySelector('.recently-viewed-products-placeholder')) {
                        console.log('🛍️ Seção de produtos vistos recentemente detectada - Processando produtos');
                        setTimeout(processRecentlyViewedSection, 500);
                    }

                    // Detecta mudanças no conteúdo das seções
                    if (mutation.type === 'childList' && mutation.target.classList) {
                        if (mutation.target.classList.contains('product-recommendations') ||
                            mutation.target.classList.contains('recently-viewed-products-placeholder')) {
                            console.log('🛍️ Conteúdo de seção atualizado - Reprocessando produtos');
                            setTimeout(() => {
                                processRecommendationsSection();
                                processRecentlyViewedSection();
                            }, 300);
                        }
                    }
                }
            });
        });
    });

    discountObserver.observe(document.body, { childList: true, subtree: true });

})();